import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppModalControls, useAppPagination } from '@/common/hooks';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
    RedirectsQueryKeys,
    useBulkDeleteRedirectsMutation,
    useBulkUpdateRedirectsActivityMutation,
    useGetAllRedirectsQuery,
} from '@/modules/Sites/Redirects';

import { RedirectsBulkManageView } from './RedirectsBulkManageView';

interface Props {
  siteId: string;
}

export const RedirectsBulkManageContainer: FC<Props> = memo(({ siteId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [deleteModalControls] = useAppModalControls();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { pageParam: currentPage, handleChangePage } = useAppPagination();

  const {
    data: redirects,
    error,
    isError,
    isLoading,
    isFetching,
  } = useGetAllRedirectsQuery(siteId, { page: currentPage })();

  const { mutate: updateActivity, isLoading: isUpdatingActivity } = useBulkUpdateRedirectsActivityMutation(siteId)({
    onSuccess: async ({ updatedCount }) => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);
      appMessageApi.success(`Оновлено редіректів: ${updatedCount}`);
      setSelectedIds([]);
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const { mutate: bulkDelete, isLoading: isDeleting } = useBulkDeleteRedirectsMutation(siteId)({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);
      appMessageApi.success('Вибрані редіректи видалено');
      setSelectedIds([]);
      deleteModalControls.close();
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const isBusy = isUpdatingActivity || isDeleting;

  const handleSetActivity = useCallback(
    (isActive: boolean) => {
      if (!selectedIds.length) return;
      updateActivity({ ids: selectedIds, isActive });
    },
    [selectedIds, updateActivity]
  );

  const handleConfirmDelete = useCallback(() => {
    bulkDelete({ ids: selectedIds });
  }, [selectedIds, bulkDelete]);

  return (
    <FallbackedLayout data={ redirects } error={ error } isError={ isError } isLoading={ isLoading }>
      { (redirectsList) => (
        <RedirectsBulkManageView
          list={ redirectsList.items }
          totalCount={ redirectsList.meta.totalItems }
          currentPage={ currentPage }
          itemsPerPage={ redirectsList.meta.itemsPerPage }
          onChangePage={ handleChangePage }
          isFetching={ isFetching }
          selectedIds={ selectedIds }
          onChangeSelectedIds={ setSelectedIds }
          isBusy={ isBusy }
          isUpdatingActivity={ isUpdatingActivity }
          isDeleting={ isDeleting }
          deleteModalControls={ deleteModalControls }
          onSetActivity={ handleSetActivity }
          onConfirmDelete={handleConfirmDelete}
        />
      ) }
    </FallbackedLayout>
  );
});
